/**
 * Fuzzy search utility for global categories
 */

export interface SearchableItem {
  id: string;
  name: string;
  tags: string[];
  description?: string;
}

/**
 * Simple fuzzy search implementation
 * Returns items that match the search term in name, description, or tags
 */
export function fuzzySearch<T extends SearchableItem>(
  items: T[],
  searchTerm: string,
  options: {
    threshold?: number; // minimum match score (0-1)
    maxResults?: number;
  } = {}
): T[] {
  const { threshold = 0.3, maxResults = 20 } = options;
  
  if (!searchTerm.trim()) {
    return items.slice(0, maxResults);
  }
  
  const normalizedSearch = searchTerm.toLowerCase().trim();
  
  const scoredItems = items.map(item => {
    let score = 0;
    let maxScore = 0;
    
    // Check name match (highest weight)
    const nameScore = calculateSimilarity(item.name.toLowerCase(), normalizedSearch);
    score += nameScore * 3;
    maxScore += 3;
    
    // Check description match (medium weight)
    if (item.description) {
      const descScore = calculateSimilarity(item.description.toLowerCase(), normalizedSearch);
      score += descScore * 2;
      maxScore += 2;
    }
    
    // Check tags match (lower weight but multiple matches)
    let tagScore = 0;
    let tagCount = 0;
    for (const tag of item.tags) {
      const tagMatch = calculateSimilarity(tag.toLowerCase(), normalizedSearch);
      if (tagMatch > 0.5) {
        tagScore += tagMatch;
        tagCount++;
      }
    }
    if (tagCount > 0) {
      score += (tagScore / tagCount) * 1.5;
      maxScore += 1.5;
    }
    
    const finalScore = maxScore > 0 ? score / maxScore : 0;
    
    return { item, score: finalScore };
  });
  
  // Filter by threshold and sort by score
  return scoredItems
    .filter(({ score }) => score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults)
    .map(({ item }) => item);
}

/**
 * Calculate similarity between two strings using Levenshtein distance
 */
function calculateSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (str1.includes(str2) || str2.includes(str1)) return 0.8;
  
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  
  if (maxLength === 0) return 1;
  
  return 1 - (distance / maxLength);
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }
  
  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Generate search tags for a category name
 * e.g., "knife" -> ["knife", "cut", "cutting", "tool", "kitchen"]
 */
export function generateSearchTags(name: string): string[] {
  const tags = [name.toLowerCase()];
  
  // Common word mappings for better search
  const wordMappings: Record<string, string[]> = {
    knife: ['cut', 'cutting', 'tool', 'kitchen', 'sharp'],
    spoon: ['eat', 'eating', 'utensil', 'kitchen', 'scoop'],
    fork: ['eat', 'eating', 'utensil', 'kitchen', 'pierce'],
    plate: ['serve', 'serving', 'dish', 'kitchen', 'food'],
    bowl: ['serve', 'serving', 'dish', 'kitchen', 'container'],
    cutting_board: ['cut', 'cutting', 'prep', 'kitchen', 'board'],
    container: ['store', 'storage', 'hold', 'box', 'jar'],
    table: ['surface', 'furniture', 'dining', 'kitchen'],
    counter: ['surface', 'kitchen', 'prep', 'work'],
    sink: ['wash', 'cleaning', 'kitchen', 'water'],
    tomato: ['fruit', 'vegetable', 'red', 'round'],
    onion: ['vegetable', 'white', 'round', 'layered'],
    banana: ['fruit', 'yellow', 'curved', 'long'],
    mold: ['fungus', 'rot', 'decay', 'spoiled', 'bad']
  };
  
  const lowerName = name.toLowerCase();
  if (wordMappings[lowerName]) {
    tags.push(...wordMappings[lowerName]);
  }
  
  // Add plural/singular variations
  if (name.endsWith('s') && name.length > 3) {
    tags.push(name.slice(0, -1).toLowerCase());
  } else {
    tags.push(name.toLowerCase() + 's');
  }
  
  return [...new Set(tags)]; // Remove duplicates
}
